import { useMemo, useState } from 'react'
import {
  AlarmClockIcon,
  ArrowLeftIcon,
  ArrowRightIcon,
  BanIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from 'lucide-react'
import { Box, Collapse, Stack, TableBody, TableHead, TableRow, Typography } from '@mui/material'

import { Button, Card, Skeleton } from '@repo/ui'
import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { ActionDropdown, type ActionDropdownItem } from 'components/input/ActionDropdown/ActionDropdown'
import { ViewToggle, type ViewToggleOption } from 'components/input/ViewToggle/ViewToggle'
import { SelectField } from 'components'
import { useSnackbar } from 'contexts'
import { useCanAccess } from 'hooks'
import {
  useDecideRecommendation,
  useRecomputeRecommendations,
  useSeoRecommendations,
  useSetSeoActionStatus,
} from 'hooks/api'
import type {
  SeoAction,
  SeoActionStatus,
  SeoRecommendation,
  SeoRecommendationsResponse,
  SeoRecStatus,
} from 'services'

import {
  Cell,
  EmptyRow,
  HeadCell,
  SectionNote,
  StatTable,
  ToneChip,
  dateTime,
  shortDate,
  type Tone,
} from '../shared'
import { fmt, fmtPct } from './seoModel'

const ADMIN_ONLY = 'Only platform admins can decide recommendations.'
const ALL = '__all'

const STATUS_ORDER: readonly SeoRecStatus[] = ['open', 'snoozed', 'approved', 'rejected', 'resolved']
const STATUS_LABEL: Record<SeoRecStatus, string> = {
  open: 'Open',
  snoozed: 'Snoozed',
  approved: 'Approved',
  rejected: 'Rejected',
  resolved: 'Resolved',
}

export const ACTION_LABEL: Record<SeoActionStatus, string> = {
  approved: 'Approved',
  in_progress: 'In progress',
  shipped: 'Shipped',
  measured: 'Measured',
}
const ACTION_ORDER: readonly SeoActionStatus[] = ['approved', 'in_progress', 'shipped', 'measured']
const ACTION_TONE: Record<SeoActionStatus, Tone> = {
  approved: 'info',
  in_progress: 'caution',
  shipped: 'positive',
  measured: 'neutral',
}
const NEXT_LABEL: Partial<Record<SeoActionStatus, string>> = {
  in_progress: 'Start work',
  shipped: 'Mark shipped',
  measured: 'Mark measured',
}

/** Primitive evidence fields worth a row, in reading order, with their labels. */
const EVIDENCE_FIELDS: ReadonlyArray<[string, string, (v: unknown) => string]> = [
  ['impressions', 'Impressions', (v) => fmt(v as number)],
  ['clicks', 'Clicks', (v) => fmt(v as number)],
  ['ctr', 'CTR', (v) => fmtPct(v as number)],
  ['expectedCtr', 'Expected CTR at that position', (v) => fmtPct(v as number)],
  ['position', 'Average position', (v) => String(v)],
  ['previousPosition', 'Previous position', (v) => String(v)],
  ['volume', 'Monthly searches', (v) => fmt(v as number)],
  ['kd', 'Keyword difficulty', (v) => String(v)],
  ['changePct', '12-month change', (v) => `+${v}%`],
  ['buyerFit', 'Buyer fit', (v) => String(v)],
  ['stepJump', 'Step jump in the series', (v) => (v ? 'yes, verify the data' : 'no')],
  ['drop', 'Places lost', (v) => String(v)],
  ['count', 'Waiting', (v) => fmt(v as number)],
  ['engine', 'Engine', (v) => String(v)],
  ['domain', 'Newly cited', (v) => String(v)],
  ['windowDays', 'Window', (v) => `${v} days`],
  ['source', 'Source', (v) => String(v)],
]

const Evidence = ({ e }: { e: Record<string, unknown> }) => {
  const rows = EVIDENCE_FIELDS.filter(([k]) => e[k] !== undefined && e[k] !== null).map(
    ([k, label, f]) => [label, f(e[k])] as const,
  )
  const queries = (e.topQueries as Array<{ query: string; impressions: number; clicks: number; position: number | null }>) ?? []
  const pages = (e.pages as Array<{ page: string; impressions: number; clicks: number; position: number | null }>) ?? []
  const competitors = (e.competitors as Array<{ domain: string; position: number | null }>) ?? []
  const overtaken = (e.overtakenBy as Array<{ domain: string; now: number | null; prev: number | null }>) ?? []
  const steps = (e.steps as string[]) ?? []
  const sample = (e.sample as string[]) ?? []
  return (
    <Stack spacing={2} sx={{ pt: 2 }}>
      {rows.length ? (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 220px) minmax(0, 1fr)' },
            columnGap: 3,
            rowGap: 1,
          }}
        >
          {rows.map(([label, value]) => (
            <Box key={label} sx={{ display: 'contents' }}>
              <Typography variant="body3" color="text.secondary">
                {label}
              </Typography>
              <Typography variant="body3">{value}</Typography>
            </Box>
          ))}
        </Box>
      ) : null}
      {queries.length || pages.length ? (
        <StatTable>
          <TableHead>
            <TableRow>
              <HeadCell>{queries.length ? 'Top queries' : 'Competing pages'}</HeadCell>
              <HeadCell align="right">Impressions</HeadCell>
              <HeadCell align="right">Clicks</HeadCell>
              <HeadCell align="right">Position</HeadCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(queries.length ? queries.map((q) => ({ ...q, label: q.query })) : pages.map((p) => ({ ...p, label: p.page }))).map((r) => (
              <TableRow key={r.label}>
                <Cell>{r.label}</Cell>
                <Cell align="right">{fmt(r.impressions)}</Cell>
                <Cell align="right">{fmt(r.clicks)}</Cell>
                <Cell align="right">{r.position ?? '—'}</Cell>
              </TableRow>
            ))}
          </TableBody>
        </StatTable>
      ) : null}
      {competitors.length ? (
        <SectionNote>
          Ranking: {competitors.map((c) => `${c.domain} #${c.position ?? '—'}`).join(', ')}
        </SectionNote>
      ) : null}
      {overtaken.length ? (
        <SectionNote>
          Moved above Vera:{' '}
          {overtaken.map((c) => `${c.domain} (#${c.prev ?? '—'} → #${c.now ?? '—'})`).join(', ')}
        </SectionNote>
      ) : null}
      {sample.length ? <SectionNote>Oldest waiting: {sample.join(', ')}</SectionNote> : null}
      {steps.length ? (
        <Stack component="ol" spacing={1} sx={{ m: 0, pl: 5 }}>
          {steps.map((s) => (
            <Typography key={s} component="li" variant="body3">
              {s}
            </Typography>
          ))}
        </Stack>
      ) : null}
    </Stack>
  )
}

const statusTone = (s: SeoRecStatus): Tone =>
  s === 'open' ? 'caution' : s === 'approved' ? 'positive' : s === 'snoozed' ? 'info' : 'neutral'

const RecommendationRow = ({ r, isAdmin }: { r: SeoRecommendation; isAdmin: boolean }) => {
  const [open, setOpen] = useState(false)
  const decide = useDecideRecommendation()
  const { open: notify } = useSnackbar()
  const undecided = r.status === 'open' || r.status === 'snoozed'
  const gate = { disabled: !isAdmin || decide.isPending, tooltip: !isAdmin ? ADMIN_ONLY : undefined }

  const run = (decision: 'approve' | 'reject' | 'snooze', days?: number) =>
    decide.mutate(
      { id: r.id, decision, days },
      {
        onSuccess: () =>
          notify({
            type: 'success',
            text:
              decision === 'approve'
                ? 'Approved. It is now a tracked action below.'
                : decision === 'reject'
                  ? 'Rejected. A recompute will not bring it back.'
                  : `Snoozed for ${days} days.`,
          }),
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )

  const snoozeItems: ActionDropdownItem[] = [7, 14, 30].map((d) => ({
    label: `Snooze ${d} days`,
    icon: <AlarmClockIcon size={18} strokeWidth={1} />,
    ...gate,
    onClick: () => run('snooze', d),
  }))

  return (
    <Box
      sx={{
        borderTop: (t) => `1px solid ${t.palette.divider}`,
        py: 4,
      }}
      data-testid={`seo-rec-${r.rule}`}
    >
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        spacing={3}
        alignItems={{ md: 'flex-start' }}
        justifyContent="space-between"
      >
        <Stack spacing={1.5} sx={{ minWidth: 0, flex: 1 }}>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <ToneChip tone="info" label={r.ruleLabel} glossaryId={`seo-rec-${r.rule.replace(/_/g, '-')}`} />
            {r.market ? <ToneChip tone="neutral" label={r.market.toUpperCase()} /> : null}
            {r.status !== 'open' ? (
              <ToneChip tone={statusTone(r.status)} label={STATUS_LABEL[r.status]} />
            ) : null}
          </Stack>
          <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere' }}>
            {r.title}
          </Typography>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            {r.suggestedAction}
          </Typography>
          <Typography variant="body3" color="text.secondary">
            {r.score >= 1
              ? `About ${fmt(Math.round(r.score))} click${Math.round(r.score) === 1 ? '' : 's'} a month at stake · `
              : ''}
            First seen {shortDate(r.firstSeenAt)}
            {r.status === 'snoozed' && r.snoozedUntil ? ` · snoozed until ${shortDate(r.snoozedUntil)}` : ''}
            {r.decidedBy && !undecided ? ` · ${STATUS_LABEL[r.status].toLowerCase()} by ${r.decidedBy}` : ''}
          </Typography>
          <Box>
            <Button
              variant="text"
              onClick={() => setOpen((v) => !v)}
              endIcon={open ? <ChevronUpIcon size={16} /> : <ChevronDownIcon size={16} />}
              sx={{ px: 0 }}
            >
              {open ? 'Hide evidence' : 'Evidence'}
            </Button>
            <Collapse in={open} unmountOnExit>
              <Evidence e={r.evidence} />
            </Collapse>
          </Box>
        </Stack>
        {undecided ? (
          <Stack direction="row" spacing={3} sx={{ flexShrink: 0 }}>
            <ActionDropdown
              items={snoozeItems}
              trigger={({ onClick }) => (
                <Button variant="secondary" onClick={onClick} disabled={gate.disabled} disabledReason={gate.tooltip} startIcon={<AlarmClockIcon size={16} strokeWidth={1.5} />}>
                  Snooze
                </Button>
              )}
            />
            <Button
              variant="secondary"
              onClick={() => run('reject')}
              disabled={gate.disabled}
              disabledReason={gate.tooltip}
              startIcon={<BanIcon size={16} strokeWidth={1.5} />}
            >
              Reject
            </Button>
            <Button
              variant="primary"
              onClick={() => run('approve')}
              disabled={gate.disabled}
              disabledReason={gate.tooltip}
              startIcon={<CheckIcon size={16} strokeWidth={1.5} />}
            >
              Approve
            </Button>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  )
}

const measurementText = (a: SeoAction): string | null => {
  const m = a.measurement
  const b = a.baseline
  if (!m) return null
  if (m.kind === 'search_console_page')
    return `Since ${m.since}: ${fmt(m.impressions as number)} impressions, ${fmt(m.clicks as number)} clicks, CTR ${fmtPct(
      m.ctr as number | null,
    )}${m.position !== null && m.position !== undefined ? `, position ${Math.round((m.position as number) * 10) / 10}` : ''}. At approval: CTR ${fmtPct(
      (b?.ctr as number | null) ?? null,
    )}, position ${b?.position ?? '—'}.`
  if (m.kind === 'serp_position')
    return `Vera now ${m.position !== null ? `#${m.position}` : 'not in the results'} (${String(m.market ?? '').toUpperCase()}).`
  return 'Measured by hand.'
}

const ActionsTable = ({ actions, isAdmin }: { actions: SeoAction[]; isAdmin: boolean }) => {
  const setStatus = useSetSeoActionStatus()
  const { open: notify } = useSnackbar()
  const move = (a: SeoAction, to: SeoActionStatus) =>
    setStatus.mutate(
      { id: a.id, status: to },
      {
        onSuccess: () => notify({ type: 'success', text: `${a.title}: ${ACTION_LABEL[to].toLowerCase()}.` }),
        onError: (e) => notify({ type: 'error', text: (e as Error).message }),
      },
    )
  return (
    <StatTable>
      <TableHead>
        <TableRow>
          <HeadCell>Action</HeadCell>
          <HeadCell>Rule</HeadCell>
          <HeadCell>Status</HeadCell>
          <HeadCell>Approved</HeadCell>
          <HeadCell>Shipped</HeadCell>
          <HeadCell>Result</HeadCell>
          <HeadCell align="right"> </HeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {actions.length === 0 ? (
          <EmptyRow colSpan={7} text="No approved recommendations yet. Approve one above and it is tracked here." />
        ) : (
          actions.map((a) => {
            const i = ACTION_ORDER.indexOf(a.status)
            const next = ACTION_ORDER[i + 1]
            const prev = ACTION_ORDER[i - 1]
            const gate = { disabled: !isAdmin || setStatus.isPending, tooltip: !isAdmin ? ADMIN_ONLY : undefined }
            const items: ActionDropdownItem[] = [
              ...(next
                ? [{ label: NEXT_LABEL[next], icon: <ArrowRightIcon size={18} strokeWidth={1} />, ...gate, onClick: () => move(a, next) }]
                : []),
              ...(prev
                ? [{ label: `Back to ${ACTION_LABEL[prev].toLowerCase()}`, icon: <ArrowLeftIcon size={18} strokeWidth={1} />, ...gate, onClick: () => move(a, prev) }]
                : []),
            ]
            return (
              <TableRow key={a.id}>
                <Cell>
                  <Box sx={{ whiteSpace: 'normal', minWidth: 240, maxWidth: 420 }}>{a.title}</Box>
                </Cell>
                <Cell>{a.ruleLabel}</Cell>
                <Cell>
                  <ToneChip tone={ACTION_TONE[a.status]} label={ACTION_LABEL[a.status]} glossaryId="seo-action" />
                </Cell>
                <Cell>{shortDate(a.approvedAt)}</Cell>
                <Cell>{shortDate(a.shippedAt)}</Cell>
                <Cell>
                  <Box sx={{ whiteSpace: 'normal', minWidth: 200, maxWidth: 360, color: 'text.secondary' }}>
                    {measurementText(a) ?? '—'}
                  </Box>
                </Cell>
                <Cell align="right">
                  {items.length ? <ActionDropdown items={items} /> : null}
                </Cell>
              </TableRow>
            )
          })
        )}
      </TableBody>
    </StatTable>
  )
}

const ThresholdsNote = ({ data }: { data: SeoRecommendationsResponse }) => {
  const t = data.thresholds
  return (
    <SectionNote>
      Rules in force: title rewrite at {fmt(t.titleMinImpressions)}+ impressions over {t.windowDays} days
      with CTR under {Math.round(t.titleCtrRatio * 100)}% of the curve, or position {t.titlePositionMin}–{t.titlePositionMax}; losing when Vera drops more than {t.losingPositionDrop} places or a
      competitor moves above her; rising at +{t.risingTrendPct}% over 12 months with {t.minVolume}+
      searches and no target page; gap terms ranked top {t.gapMaxPosition} by an approved competitor
      (best {t.gapMaxItems}); cannibalisation when two Vera URLs each earn {t.cannibalMinImpressions}+
      impressions for one query. Thresholds are SEO_REC_* settings on the API.
    </SectionNote>
  )
}

/**
 * VRG-643 follow-up (Spencer, 2026-10-03) — Recommendations: the step of the SEO loop
 * where the data becomes decisions. The API recomputes after every scheduled run; a
 * platform admin approves (it becomes a tracked action), rejects (it stays rejected) or
 * snoozes. Approved actions move approved → in progress → shipped → measured, and
 * "measured" stores what the metric reads beside what it read at approval.
 */
export const SeoRecommendationsCard = () => {
  const { isHasAccess: isAdmin } = useCanAccess({ role: 'platform_admin' })
  const { data, isLoading, error } = useSeoRecommendations()
  const recompute = useRecomputeRecommendations()
  const { open: notify } = useSnackbar()
  const [status, setStatus] = useState<SeoRecStatus>('open')
  const [rule, setRule] = useState<string>(ALL)

  const statusOptions: readonly ViewToggleOption<SeoRecStatus>[] = useMemo(
    () =>
      STATUS_ORDER.map((s) => ({
        value: s,
        label: data ? `${STATUS_LABEL[s]} · ${fmt(data.counts[s] ?? 0)}` : STATUS_LABEL[s],
      })),
    [data],
  )
  const items = (data?.items ?? []).filter(
    (r) => r.status === status && (rule === ALL || r.rule === rule),
  )

  return (
    <Stack spacing={6}>
      <Card
        title="Recommendations"
        action={
          <Button
            variant="secondary"
            disabled={!isAdmin || recompute.isPending}
            disabledReason={!isAdmin ? ADMIN_ONLY : undefined}
            onClick={() =>
              recompute.mutate(undefined, {
                onSuccess: (r) =>
                  notify({
                    type: 'success',
                    text: r.skipped
                      ? 'Another recompute is running. Reload in a moment.'
                      : `Recomputed: ${r.firing} firing, ${r.inserted} new, ${r.resolved} resolved.`,
                  }),
                onError: (e) => notify({ type: 'error', text: (e as Error).message }),
              })
            }
          >
            Recompute now
          </Button>
        }
      >
        <Stack spacing={4} sx={{ mt: 4 }}>
          {isLoading ? (
            <Skeleton height={14} lines={8} />
          ) : error || !data ? (
            <SectionNote>Could not load recommendations: {(error as Error | null)?.message}</SectionNote>
          ) : (
            <>
              <SectionNote>
                Recomputed after every scheduled job (last {data.lastComputedAt ? dateTime(data.lastComputedAt) : 'never'}).
                Ordered by an estimate of monthly clicks at stake
                <InfoTooltip term="seo-recommendation-score" />. Approve to track an item through to
                a measured result; reject and it does not come back; snooze and it reopens later if it
                still applies.
              </SectionNote>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={3}
                justifyContent="space-between"
                alignItems={{ md: 'center' }}
              >
                <ViewToggle options={statusOptions} value={status} onChange={setStatus} ariaLabel="Recommendation status" />
                <Box sx={{ minWidth: 220 }}>
                  <SelectField
                    value={rule}
                    onChange={(v) => setRule(v as string)}
                    options={[
                      { value: ALL, label: 'Every rule' },
                      ...data.rules.map((r) => ({ value: r.rule, label: r.label })),
                    ]}
                  />
                </Box>
              </Stack>
              <Box>
                {items.length === 0 ? (
                  <SectionNote>
                    {status === 'open'
                      ? 'Nothing to decide. The next scheduled run recomputes, or press Recompute now.'
                      : `No ${STATUS_LABEL[status].toLowerCase()} recommendations${rule === ALL ? '' : ' for this rule'}.`}
                  </SectionNote>
                ) : (
                  items.map((r) => <RecommendationRow key={r.id} r={r} isAdmin={isAdmin} />)
                )}
              </Box>
              <ThresholdsNote data={data} />
            </>
          )}
        </Stack>
      </Card>
      <Card title="Tracked actions">
        <Stack spacing={4} sx={{ mt: 4 }}>
          <SectionNote>
            Every approved recommendation, from approval to a measured result
            <InfoTooltip term="seo-action" />. Move one step at a time from the row menu; marking an
            action measured records the same metric again, beside its value at approval.
          </SectionNote>
          {isLoading ? <Skeleton height={14} lines={4} /> : <ActionsTable actions={data?.actions ?? []} isAdmin={isAdmin} />}
        </Stack>
      </Card>
    </Stack>
  )
}
